import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Part, PriceInfo } from "./models/parts";
import { Parameter } from "./models/parameters";
import {
  ContainerType,
  MSLLevel,
} from "../components/component-search/PartDrawer/Inventory/PhysicalInventory/AddNewContainer";

export interface Offering {
  inventory_level: number;
  packaging: string | null;
  distributor_link: string;
  minimum_order_quantity: number;
  price_breaks: PriceInfo[];
}

export interface SellerInventory {
  seller: string;
  octopart_seller_id: string;
  inventory_level: number;
  offerings: Offering[];
}

export interface InventoryOverride {
  inventory_level: number;
  minimum_order_quantity: number;
  expiration: string;
  price_breaks: PriceInfo[];
  notes: string;
}

export interface InventoryOverrideRecord {
  user_id: string;
  datetime_set: string;
  snapshot: string;
  override: InventoryOverride | null;
}

export interface VirtualInventory {
  override: InventoryOverride | null;
  override_history: InventoryOverrideRecord[];
  sellers_inventory: SellerInventory[];
  unapproved_sellers_inventory: SellerInventory[];
}

export interface ContainerAllocation {
  quantity_allocated: number;
  board_id: string;
  shipment_id: string;
  locked: boolean;
}

export interface ContainerFeederSummary {
  flashpcb_container_barcode: string;
  flashpcb_bin_barcode: string;
  bin_name: string;
  bin_location: string;
}

export interface Container {
  part_name: string;
  part_id: string;
  container_type: ContainerType;
  available_inventory_level: number;
  last_counted_amount_less_spent_allocations: number;
  last_counted_amount: number;
  notes: string | null;
  nickname: string | null;
  price_info: PriceInfo;
  order_date: string | null;
  vendor: string | null;
  moisture_sensitivity_level: MSLLevel | null;
  flashpcb_container_barcode: string;
  // TODO: don't retrieve the bin for container
  flashpcb_bin_barcode: string;
  bin_location: string;
  bin_name: string;
  bin_type: string;
  can_allocate_from_bin: boolean;
  part_allocations: ContainerAllocation[];
  spent_part_allocations: ContainerAllocation[];
  active_allocations_total: number;
  spent_allocations_total: number;
  consignment_container_owner_id: string | null;
  country_code: string | null;
  lot_number: string | null;
  date_code: string | null;
  most_recent_update: string | null;
  containers_on_feeders: ContainerFeederSummary[] | null;
  reel_width_mm: number | null;
  reel_pitch_mm: number | null;
}

export interface PhysicalInventory {
  inventory_level: number;
  active_allocations_total: number;
  spent_allocations_total: number;
  last_counted_amount_total: number;
  containers: Container[];
  price_info?: PriceInfo;
  // Shipment id to how many of the part that shipment has used up so far
  amounts_spent: Record<string, number>;
}

export interface InventoryResponse {
  virtual_inventory: VirtualInventory;
  physical_inventory: PhysicalInventory;
  inventory_level: number;
  price_info: PriceInfo | null;
}

export interface Attribute {
  name: string;
}

export interface PropertyAggregateBucket {
  displayValue: string;
  floatValue: number | null;
  count: number | null;
}

export interface PropertyAggregate {
  attribute: Attribute;
  buckets: PropertyAggregateBucket[];
}

export interface ParameterAggregate {
  parameter: Parameter;
  property_aggregate: PropertyAggregate;
}

export type FilterValue = string;

export type FilterProperties = {
  [shortName: string]: FilterValue[] | undefined;
};

export const CATEGORY_ID_FILTER = "category_id";

export interface SearchResponse {
  parts: Part[];
  parameter_aggregates: ParameterAggregate[];
  max_page: number;
  starting_index: number;
  inventory_per_page: number;
  total_count: number;
}

// Mirrors PartSearchSort in the backend. Sorting only applies to internal
// searches (in stock only or FlashPCB barcode searches); octopart searches
// cannot sort by our physical inventory.
export enum PartSearchSort {
  PHYSICAL_INVENTORY_LEVEL = "physical_inventory_level",
}

export interface SearchParameters {
  page: number;
  searchTerms: string;
  categoryIds: string[];
  filters: FilterProperties;
  inStockOnly: boolean;
  sort: PartSearchSort | null;
}

export const componentSearchSlice = createSlice({
  name: "categories",
  initialState: {
    pendingFilters: {},
    searchParameters: null,
    loading: false,
    errored: false,
    searchResponse: null,
  } as {
    pendingFilters: FilterProperties;
    searchParameters: SearchParameters | null;
    loading: boolean;
    errored: boolean;
    searchResponse: SearchResponse | null;
  },
  reducers: {
    startSearch: (
      state,
      action: PayloadAction<{ searchParameters: SearchParameters }>,
    ) => {
      state.loading = true;
      state.searchParameters = action.payload.searchParameters;
      state.pendingFilters = action.payload.searchParameters.filters;
    },
    addToPendingFilters: (
      state,
      action: PayloadAction<{
        parameterShortName: string;
        rawValue: FilterValue;
      }>,
    ) => {
      if (
        state.pendingFilters[action.payload.parameterShortName] === undefined
      ) {
        state.pendingFilters[action.payload.parameterShortName] = [];
      }
      (
        state.pendingFilters[action.payload.parameterShortName] as FilterValue[]
      ).push(action.payload.rawValue);
    },
    removeFromPendingFilters: (
      state,
      action: PayloadAction<{
        parameterShortName: string;
        rawValue: FilterValue;
      }>,
    ) => {
      state.pendingFilters[action.payload.parameterShortName] = (
        state.pendingFilters[action.payload.parameterShortName] as FilterValue[]
      ).filter((value) => value != action.payload.rawValue);
      if (
        (
          state.pendingFilters[
            action.payload.parameterShortName
          ] as FilterValue[]
        ).length === 0
      ) {
        delete state.pendingFilters[action.payload.parameterShortName];
      }
    },
    loadSearchResponse: (
      state,
      action: PayloadAction<{
        searchResponse: SearchResponse;
      }>,
    ) => {
      state.searchResponse = action.payload.searchResponse;
      state.loading = false;
    },
    searchError: (state) => {
      state.errored = true;
      state.loading = false;
    },
    clearSearch: (state) => {
      state.pendingFilters = {};
      state.loading = false;
      state.errored = false;
      state.searchResponse = null;
      state.searchParameters = null;
    },
  },
});

export const {
  startSearch,
  searchError,
  addToPendingFilters,
  removeFromPendingFilters,
  loadSearchResponse,
  clearSearch,
} = componentSearchSlice.actions;

export default componentSearchSlice.reducer;
